// @ts-nocheck
/**
 * Assignment Submit Page
 * Assignment file submission interface with Carbon ProgressBar
 * Epic 75 - Work Item 3: R2 Object Storage and Content Delivery Architecture
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './AssignmentSubmit.css';
import {
  Grid,
  Column,
  Breadcrumb,
  BreadcrumbItem,
  Form,
  FormGroup,
  TextArea,
  Button,
  Tag,
  InlineNotification,
  ProgressBar,
  Tile,
  Modal,
  Checkbox,
  Heading,
} from '@carbon/react';
import { Upload, Document, Time, Calendar, CheckmarkFilled, WarningFilled, InProgress } from '@carbon/react/icons';
import { FileUploader } from '../components/storage/FileUploader';

interface Assignment {
  id: string;
  title: string;
  description: string;
  courseId: string;
  courseName: string;
  dueDate: string;
  maxScore: number;
  attachmentRequired: boolean;
  maxFileSize: number;
  allowedFileTypes: string[];
  status: 'pending' | 'submitted' | 'graded' | 'late';
  submission?: AssignmentSubmission;
}

interface AssignmentSubmission {
  id: string;
  submittedAt: string;
  fileIds: string[];
  files: SubmissionFile[];
  comments: string;
  score?: number;
  feedback?: string;
  status: 'submitted' | 'graded' | 'returned';
}

interface SubmissionFile {
  fileId: string;
  fileName: string;
  fileSize: number;
  fileUrl: string;
}

const AssignmentSubmit: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId?: string }>();
  const navigate = useNavigate();

  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Submission form
  const [comments, setComments] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const endpoint = assignmentId ? `/api/v1/assignments/${assignmentId}` : '/api/v1/assignments/pending';

      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch assignments');
      }

      const data = await response.json();

      if (assignmentId) {
        setSelectedAssignment(data);
      } else {
        setAssignments(data.assignments);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, [assignmentId]);

  const handleFileUpload = (fileData: any) => {
    setUploadedFiles(prev => [...prev, fileData.fileId]);
  };

  const handleSubmit = async () => {
    if (!selectedAssignment) return;

    if (selectedAssignment.attachmentRequired && uploadedFiles.length === 0) {
      setError('Please upload at least one file for this assignment');
      return;
    }

    if (!agreedToTerms) {
      setError('Please confirm that this is your original work');
      return;
    }

    setConfirmModalOpen(true);
  };

  const confirmSubmission = async () => {
    if (!selectedAssignment) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/v1/assignments/${selectedAssignment.id}/submit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileIds: uploadedFiles,
          comments,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit assignment');
      }

      setSuccess(true);
      setConfirmModalOpen(false);

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate(`/courses/${selectedAssignment.courseId}/assignments`);
      }, 3000);
    } catch (err: any) {
      setError(err.message);
      setConfirmModalOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (
    status: string
  ):
    | 'red'
    | 'magenta'
    | 'purple'
    | 'blue'
    | 'cyan'
    | 'teal'
    | 'green'
    | 'gray'
    | 'cool-gray'
    | 'warm-gray'
    | 'high-contrast'
    | 'outline' => {
    const colors: Record<
      string,
      | 'red'
      | 'magenta'
      | 'purple'
      | 'blue'
      | 'cyan'
      | 'teal'
      | 'green'
      | 'gray'
      | 'cool-gray'
      | 'warm-gray'
      | 'high-contrast'
      | 'outline'
    > = {
      pending: 'gray',
      submitted: 'blue',
      graded: 'green',
      late: 'red',
      returned: 'purple',
    };
    return colors[status] || 'gray';
  };

  const getStatusIcon = (status: string) => {
    const iconProps = { size: 16 };
    switch (status) {
      case 'submitted':
        return <CheckmarkFilled {...iconProps} className="assignment-status-icon assignment-status-icon--info" />;
      case 'graded':
        return <CheckmarkFilled {...iconProps} className="assignment-status-icon assignment-status-icon--success" />;
      case 'late':
        return <WarningFilled {...iconProps} className="assignment-status-icon assignment-status-icon--error" />;
      default:
        return <InProgress {...iconProps} className="assignment-status-icon assignment-status-icon--secondary" />;
    }
  };

  const getDaysRemaining = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diff = due.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days < 0) return { text: 'Overdue', className: 'assignment-due-date--error' };
    if (days === 0) return { text: 'Due today', className: 'assignment-due-date--warning' };
    if (days === 1) return { text: 'Due tomorrow', className: 'assignment-due-date--warning' };
    if (days <= 3) return { text: `${days} days remaining`, className: 'assignment-due-date--warning' };
    return { text: `${days} days remaining`, className: 'assignment-due-date--secondary' };
  };

  if (loading) {
    return (
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <div className="assignment-loading">
            <InProgress size={32} className="assignment-loading-spinner" />
          </div>
        </Column>
      </Grid>
    );
  }

  if (error && !selectedAssignment) {
    return <InlineNotification kind="error" title="Error loading assignments" subtitle={error} />;
  }

  return (
    <div className="assignment-submit-page">
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          {/* Breadcrumb */}
          <Breadcrumb>
            <BreadcrumbItem href="/">Home</BreadcrumbItem>
            <BreadcrumbItem href="/dashboard">Dashboard</BreadcrumbItem>
            <BreadcrumbItem href="/assignments">Assignments</BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>{selectedAssignment ? 'Submit' : 'Select Assignment'}</BreadcrumbItem>
          </Breadcrumb>

          {/* Page Header */}
          <div className="assignment-page-header">
            <Heading size="lg" className="assignment-page-title">
              Submit Assignment
            </Heading>
            <p className="assignment-page-description">Upload your assignment files and submit for grading</p>
          </div>

          {success && (
            <InlineNotification
              kind="success"
              title="Assignment Submitted Successfully!"
              subtitle="Your assignment has been submitted for grading. Redirecting..."
            />
          )}

          {error && (
            <InlineNotification kind="error" title="Submission Error" subtitle={error} onClose={() => setError(null)} />
          )}

          {!selectedAssignment && !assignmentId ? (
            /* Assignment Selection */
            <div>
              <Heading size="md" className="assignment-section-heading">
                Select an Assignment to Submit
              </Heading>
              <Grid fullWidth>
                {assignments.map(assignment => {
                  const daysInfo = getDaysRemaining(assignment.dueDate);
                  return (
                    <Column key={assignment.id} lg={8} md={4} sm={4}>
                      <Tile className="assignment-tile" onClick={() => setSelectedAssignment(assignment)}>
                        <div className="assignment-tile-header">
                          <div>
                            <Heading size="sm">{assignment.title}</Heading>
                            <p className="assignment-course-name">{assignment.courseName}</p>
                          </div>
                          <div className="assignment-status-group">
                            {getStatusIcon(assignment.status)}
                            <Tag kind={getStatusColor(assignment.status)} size="sm">
                              {assignment.status}
                            </Tag>
                          </div>
                        </div>

                        <p className="assignment-description">{assignment.description}</p>

                        <div className="assignment-tile-info">
                          <div className="assignment-info-item">
                            <Calendar size={16} />
                            <span className={daysInfo.className}>{daysInfo.text}</span>
                          </div>
                          <div className="assignment-info-item">
                            <Document size={16} />
                            <span>{assignment.maxScore} points</span>
                          </div>
                        </div>

                        {assignment.submission && (
                          <div
                            style={{
                              marginTop: 'var(--cds-spacing-04)',
                              paddingTop: 'var(--cds-spacing-04)',
                              borderTop: '1px solid var(--cds-border-subtle)',
                            }}
                          >
                            <p className="cds--type-body-compact-01" style={{ color: 'var(--cds-support-info)' }}>
                              Submitted on {new Date(assignment.submission.submittedAt).toLocaleDateString()}
                            </p>
                            {assignment.submission.score !== undefined && (
                              <p className="cds--type-body-compact-01 cds--font-semibold">
                                Score: {assignment.submission.score}/{assignment.maxScore}
                              </p>
                            )}
                          </div>
                        )}
                      </Tile>
                    </Column>
                  );
                })}
              </Grid>
            </div>
          ) : selectedAssignment ? (
            /* Assignment Submission Form */
            <Grid narrow>
              <Column lg={10} md={8} sm={4}>
                <div className="assignment-details" style={{ marginBottom: 'var(--cds-spacing-07)' }}>
                  <Tile style={{ marginBottom: 'var(--cds-spacing-05)' }}>
                    <div className="flex justify-between items-start" style={{ marginBottom: 'var(--cds-spacing-05)' }}>
                      <div>
                        <h2 className="cds--type-heading-03" style={{ marginBottom: 'var(--cds-spacing-03)' }}>
                          {selectedAssignment.title}
                        </h2>
                        <p className="cds--type-body-01" style={{ color: 'var(--cds-text-secondary)' }}>
                          {selectedAssignment.courseName}
                        </p>
                      </div>
                      <Tag kind={getStatusColor(selectedAssignment.status)}>{selectedAssignment.status}</Tag>
                    </div>

                    <div className="prose max-w-none" style={{ marginBottom: 'var(--cds-spacing-05)' }}>
                      <h3 className="cds--type-heading-compact-02" style={{ marginBottom: 'var(--cds-spacing-03)' }}>
                        Instructions
                      </h3>
                      <p
                        className="cds--type-body-01"
                        style={{ color: 'var(--cds-text-primary)', whiteSpace: 'pre-wrap' }}
                      >
                        {selectedAssignment.description}
                      </p>
                    </div>

                    <Grid fullWidth className="cds--type-body-compact-01">
                      <Column lg={8} md={4} sm={4}>
                        <span style={{ color: 'var(--cds-text-secondary)' }}>Due Date:</span>
                        <p className="cds--font-semibold">{new Date(selectedAssignment.dueDate).toLocaleString()}</p>
                      </Column>
                      <Column lg={8} md={4} sm={4}>
                        <span style={{ color: 'var(--cds-text-secondary)' }}>Max Score:</span>
                        <p className="cds--font-semibold">{selectedAssignment.maxScore} points</p>
                      </Column>
                      <Column lg={8} md={4} sm={4}>
                        <span style={{ color: 'var(--cds-text-secondary)' }}>File Requirements:</span>
                        <p className="cds--font-semibold">
                          {selectedAssignment.attachmentRequired ? 'Required' : 'Optional'}
                        </p>
                      </Column>
                      <Column lg={8} md={4} sm={4}>
                        <span style={{ color: 'var(--cds-text-secondary)' }}>Max File Size:</span>
                        <p className="cds--font-semibold">
                          {(selectedAssignment.maxFileSize / (1024 * 1024)).toFixed(0)} MB
                        </p>
                      </Column>
                    </Grid>

                    {selectedAssignment.allowedFileTypes.length > 0 && (
                      <div style={{ marginTop: 'var(--cds-spacing-05)' }}>
                        <span className="cds--type-body-compact-01" style={{ color: 'var(--cds-text-secondary)' }}>
                          Accepted formats:
                        </span>
                        <div className="flex flex-wrap gap-1" style={{ marginTop: 'var(--cds-spacing-02)' }}>
                          {selectedAssignment.allowedFileTypes.map(type => (
                            <Tag key={type} kind="gray" size="sm">
                              {type}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}
                  </Tile>

                  {/* Previous Submission */}
                  {selectedAssignment.submission && (
                    <InlineNotification
                      kind="info"
                      title="Previous Submission Found"
                      subtitle={`You submitted this assignment on ${new Date(
                        selectedAssignment.submission.submittedAt
                      ).toLocaleString()}. Submitting again will replace your previous submission.`}
                      style={{ marginBottom: 'var(--cds-spacing-05)' }}
                    />
                  )}

                  {/* Submission Form */}
                  <Form
                    onSubmit={e => {
                      e.preventDefault();
                      handleSubmit();
                    }}
                    className="assignment-submission-form"
                  >
                    <div className="assignment-form-section">
                      <FormGroup legendText="Upload Assignment Files">
                        <FileUploader
                          onUploadComplete={handleFileUpload}
                          category="assignment"
                          maxFileSize={selectedAssignment.maxFileSize}
                          allowedTypes={selectedAssignment.allowedFileTypes}
                          assignmentId={selectedAssignment.id}
                        />
                        {uploadedFiles.length > 0 && (
                          <div className="assignment-upload-success">
                            <p className="assignment-upload-success-text">
                              {uploadedFiles.length} file(s) uploaded successfully
                            </p>
                          </div>
                        )}
                      </FormGroup>
                    </div>

                    <div className="assignment-form-section">
                      <TextArea
                        id="comments"
                        labelText="Additional Comments (Optional)"
                        placeholder="Add any notes or comments about your submission..."
                        value={comments}
                        onChange={e => setComments(e.target.value)}
                        rows={4}
                      />
                    </div>

                    <div className="assignment-form-section">
                      <Checkbox
                        id="terms"
                        labelText="I confirm that this is my original work and I have properly cited all sources"
                        checked={agreedToTerms}
                        onChange={(_, { checked }) => setAgreedToTerms(checked)}
                      />
                    </div>

                    <div className="assignment-form-actions">
                      <Button
                        kind="primary"
                        type="submit"
                        disabled={
                          submitting ||
                          !agreedToTerms ||
                          (selectedAssignment.attachmentRequired && uploadedFiles.length === 0)
                        }
                        renderIcon={Upload}
                      >
                        Submit Assignment
                      </Button>
                      <Button
                        kind="secondary"
                        onClick={() => {
                          if (!assignmentId) {
                            setSelectedAssignment(null);
                          } else {
                            navigate('/assignments');
                          }
                        }}
                      >
                        Cancel
                      </Button>
                    </div>

                    {submitting && (
                      <div className="assignment-form-section">
                        <ProgressBar
                          label="Submitting assignment..."
                          helperText="Please wait while we process your submission"
                          value={100}
                        />
                      </div>
                    )}
                  </Form>
                </div>
              </Column>

              {/* Sidebar Info */}
              <Column lg={6} md={8} sm={4}>
                <div style={{ position: 'sticky', top: 'var(--cds-spacing-05)' }}>
                  <Tile style={{ marginBottom: 'var(--cds-spacing-05)' }}>
                    <h3
                      className="cds--font-semibold flex items-center"
                      style={{ marginBottom: 'var(--cds-spacing-04)', gap: 'var(--cds-spacing-03)' }}
                    >
                      <Time size={20} />
                      Submission Deadline
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--cds-spacing-03)' }}>
                      {(() => {
                        const daysInfo = getDaysRemaining(selectedAssignment.dueDate);
                        return (
                          <>
                            <p className="cds--type-heading-compact-02" style={{ color: daysInfo.color }}>
                              {daysInfo.text}
                            </p>
                            <p className="cds--type-body-compact-01" style={{ color: 'var(--cds-text-secondary)' }}>
                              Due: {new Date(selectedAssignment.dueDate).toLocaleString()}
                            </p>
                          </>
                        );
                      })()}
                    </div>
                  </Tile>

                  <Tile>
                    <h3 className="cds--type-heading-compact-02" style={{ marginBottom: 'var(--cds-spacing-04)' }}>
                      Submission Tips
                    </h3>
                    <ul className="cds--type-body-compact-01" style={{ color: 'var(--cds-text-secondary)' }}>
                      <li>• Review all requirements before submitting</li>
                      <li>• Ensure your files are in the correct format</li>
                      <li>• Double-check file sizes don't exceed limits</li>
                      <li>• Save your work frequently</li>
                      <li>• Keep a backup copy of your submission</li>
                      <li>• Submit early to avoid last-minute issues</li>
                    </ul>
                  </Tile>
                </div>
              </Column>
            </Grid>
          ) : null}
        </Column>
      </Grid>

      {/* Confirmation Modal */}
      <Modal
        open={confirmModalOpen}
        onRequestClose={() => setConfirmModalOpen(false)}
        modalHeading="Confirm Submission"
        primaryButtonText="Submit"
        secondaryButtonText="Cancel"
        onRequestSubmit={confirmSubmission}
        danger={false}
      >
        <div className="assignment-modal-content">
          <p>
            Are you sure you want to submit this assignment?
            {selectedAssignment?.submission && <strong> This will replace your previous submission.</strong>}
          </p>
          <Tile>
            <Heading size="sm" className="assignment-modal-summary-title">
              Submission Summary:
            </Heading>
            <ul className="assignment-modal-summary-list">
              <li>• {uploadedFiles.length} file(s) attached</li>
              {comments && <li>• Comments included</li>}
              <li>• Original work confirmed</li>
            </ul>
          </Tile>
          <p className="assignment-modal-disclaimer">
            Once submitted, you cannot edit this submission unless the instructor returns it for revision.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default AssignmentSubmit;
